import { useCallback, useEffect, useRef, useState } from "react";

import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { CircularProgress, Fade, Tooltip } from "@mui/material";

import { ButtonVariant } from "Constant/button";
import { useDispatch } from "react-redux";

import StandartButton from "components/atoms/StandartButton";
import { updateConnectionName } from "helpers/leads/leadConnection/updateConnectionName";

import { setSingleConnectionName } from "../../redux/slices/leadConnection/leadConnection.slice";

const InlineConnectionNameEdit = ({ connectionId, name, loading = false }) => {
    const dispatch = useDispatch();
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(name || "");
    const [saving, setSaving] = useState(false);
    const lastSavedRef = useRef(name || "");
    const inputRef = useRef(null);

    useEffect(() => {
        if ((name || "") !== lastSavedRef.current) {
            setValue(name || "");
            lastSavedRef.current = name || "";
        }
    }, [name]);

    const commitSave = useCallback(async () => {
        const trimmed = (value || "").trim();
        if (!trimmed) return false;
        if (trimmed === lastSavedRef.current) {
            setEditing(false);
            return true;
        }
        setSaving(true);
        const response = await updateConnectionName(connectionId, {
            name: trimmed,
        });
        setSaving(false);
        if (response.status) {
            lastSavedRef.current = trimmed;
            setValue(trimmed);
            dispatch(setSingleConnectionName(trimmed));
            setEditing(false);
            return true;
        }
        return false;
    }, [connectionId, dispatch, value]);

    useEffect(() => {
        if (editing) inputRef.current?.focus();
    }, [editing]);

    if (loading) return null;

    const revertAndClose = () => {
        setValue(lastSavedRef.current);
        setEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            commitSave();
        } else if (e.key === "Escape") {
            revertAndClose();
        }
    };

    if (editing) {
        return (
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                }}
            >
                <input
                    ref={inputRef}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onBlur={revertAndClose}
                    onKeyDown={handleKeyDown}
                    style={{
                        font: "inherit",
                        color: "inherit",
                        border: "none",
                        borderBottom: "1px solid #ed6c02",
                        outline: "none",
                        background: "transparent",
                        padding: 0,
                        minWidth: 160,
                        maxWidth: 420,
                    }}
                />
                <StandartButton
                    variant={ButtonVariant.GHOST}
                    iconButton
                    noBorder
                    aria-label="Save connection name"
                    disabled={saving}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={commitSave}
                >
                    {saving ? (
                        <CircularProgress size={16} color="inherit" />
                    ) : (
                        <CheckIcon fontSize="small" />
                    )}
                </StandartButton>
            </span>
        );
    }

    return (
        <Tooltip title="Click to edit connection name">
            <Fade in timeout={400}>
                <span
                    style={{ display: "inline-flex", alignItems: "center" }}
                >
                    <span
                        onClick={() => setEditing(true)}
                        style={{ cursor: "pointer" }}
                    >
                        Name: {value}
                    </span>
                    <StandartButton
                        variant={ButtonVariant.GHOST}
                        iconButton
                        noBorder
                        aria-label="Edit connection name"
                        onClick={() => setEditing(true)}
                    >
                        <EditOutlinedIcon fontSize="small" />
                    </StandartButton>
                </span>
            </Fade>
        </Tooltip>
    );
};

export default InlineConnectionNameEdit;
